import React, { useEffect } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import { useNavigate, useParams } from 'react-router-dom'
import '../../../assets/styles/custom-styles.css'
import useLoader from '../../../hooks/useLoader'
import useForm from '../../../hooks/useForm'
import { setDefaultFieldValues } from '../../../utils/objectUtils'
import DetailsSectionRenderer from '../../../components/DetailsSectionRenderer'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import PageHeader from '../../../components/PageHeader'
import TableDetails from '../../../components/table/TableDetails'
import { detailsBillingFields } from '../../../constants/forms/faturamento/billFields'
import ListHeader from '../../../components/ListHeader'
import { usePermissions } from '../../../hooks/usePermissions'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { getBillingLabel } from '../../../enums/billing-enum'

const DetailsBillPage = () => {
  const { canAccess } = usePermissions()
  const navigate = useNavigate()
  const { id } = useParams()
  const { showLoader, hideLoader } = useLoader()
  const [billingSection] = detailsBillingFields
  const { formData, setFormData } = useForm(
    setDefaultFieldValues([billingSection]),
    [billingSection]
  )

  const { get: baseServiceGet, getByColumn: baseServiceGetByColumn } =
    useBaseService(navigate)

  // Busca os dados do faturamento
  const {
    data: billData,
    isLoading: isLoadingBillData,
    isError: isErrorBillData,
  } = useQueryWithLoader({
    queryKey: ['billing', id],
    queryFn: async () => {
      showLoader()
      const response = await baseServiceGetByColumn(
        entities.billing.getById(id)
      )
      return response.result
    },
    enabled: !!id,
    select: (data) => {
      return {
        billing: {
          number: data.number ?? '-',
          contract: `${data.contract?.number} - ${data.contract?.name}` ?? '-',
          customer: data.customer?.alias ?? '-',
          created_by: data.created_by?.name,
          billing_date: data.billing_date ?? '-',
          start_date: data.start_date ?? '-',
          end_date: data.end_date ?? '-',
          status: getBillingLabel(data.status) ?? '-',
        },
      }
    },
  })

  // Busca os itens do faturamento
  const { data: itemsData, isLoading: isLoadingItemsData } = useQueryWithLoader(
    {
      queryKey: ['billingItems', id],
      queryFn: async () => {
        showLoader()
        const filterTransformed = transformFiltersForUrl({
          billing_id: id,
          deleted_at: false,
        })
        const params = { filters: filterTransformed, perPage: 1000 }
        const response = await baseServiceGet(
          entities.billing.items.get(id),
          params
        )
        return response.result.data
      },
      enabled: !!id,
      select: (data) => {
        return {
          items: data.map((item) => ({
            alias: item.alias ?? '-',
            description: item.description ?? '-',
            quantity: item.quantity ?? '-',
            price: item.price ?? '-',
          })),
        }
      },
      refetchOnMount: true,
    }
  )

  useEffect(() => {
    if (!isLoadingBillData && !isLoadingItemsData) {
      hideLoader()
    }

    if (billData) {
      setFormData({
        billing: billData.billing,
        items: itemsData?.items || [],
      })
    }
  }, [
    isErrorBillData,
    billData,
    isLoadingBillData,
    isLoadingItemsData,
    hideLoader,
    setFormData,
    itemsData?.items,
  ])

  const itemsColumns = [
    { key: 'alias', label: 'Item' },
    { key: 'description', label: 'Descrição' },
    {
      key: 'quantity',
      label: 'Quantidade',
      align: 'center',
    },
    {
      key: 'price',
      label: 'Preço Unit.',
      isMoney: true,
      align: 'right',
    },
    {
      key: 'total',
      label: 'Total',
      numeric: true,
      isMoney: true,
      align: 'right',
      // Calcula o total da linha
      render: (value, row) => {
        const rowTotal = (Number(row.quantity) || 0) * (Number(row.price) || 0)
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(rowTotal)
      },
      // Função para o cálculo do total geral no rodapé
      totalCalc: (row) =>
        (Number(row.quantity) || 0) * (Number(row.price) || 0),
    },
  ]

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Detalhes do Faturamento"
        showBackButton={true}
        backUrl="/faturamento/faturar/"
      />

      <div className="container-fluid p-1">
        <DetailsSectionRenderer
          formData={formData}
          sections={[billingSection]}
          readOnly
        />
        <div className="mt-4 shadow-sm rounded">
          <div className="my-2">
            <ListHeader
              title="Equipamentos"
              buttonIsNotVisible={true}
              canAccess={canAccess}
            />
          </div>
          <TableDetails data={formData?.items || []} columns={itemsColumns} />
        </div>
      </div>
    </MainLayout>
  )
}

export default DetailsBillPage
